import { Select, MenuItem, FormControl, InputLabel, FormHelperText } from '@mui/material';
import { updateOfferThunk, getOfferByIdThunk, getAllOffersThunk } from '@redux/features/offer.slice';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import React, { FC, useEffect } from 'react';
import * as Yup from 'yup';

type IProps = {
    show: boolean;
    handleClose: () => void;
    offerId: number;
    productId: number;
};

const UpdateOfferModal: FC<IProps> = ({ show, handleClose, offerId, productId }) => {
    const { specificOfferLoading, offer, updateOfferLoading } = useAppSelector((state: RootState) => state.offerState);
    // ----------------------------------------------------------------
    const dispatch = useAppDispatch();
    // --------------------------------------------------
    const validationSchema = Yup.object({
        numberOfStatic: Yup.string()
            .oneOf(
                ['STATIC1', 'STATIC2', 'STATIC3', 'STATIC4', 'STATIC5', 'STATIC6', 'STATIC7', 'STATIC8', 'NO_STATIC'],
                'Please select a valid option'
            )
            .required('Number Of Static Is Required'),
    });
    const handleSubmit = (values: any) => {
        dispatch(updateOfferThunk({ id: productId, body: { numberOfStatic: values.numberOfStatic } })).then(
            (res: any) => {
                if (res.meta.requestStatus === 'fulfilled') {
                    dispatch(getAllOffersThunk());
                    handleClose();
                    formik.resetForm();
                }
            }
        );
    };
    // --------------------------------------------------
    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            numberOfStatic: offer?.numberOfStatic || ``,
        },
        validationSchema,
        onSubmit: handleSubmit,
    });
    // --------------------------------------------------
    useEffect(() => {
        if (show && offerId) {
            dispatch(getOfferByIdThunk(offerId));
        }
    }, [show, offerId, dispatch]);

    useEffect(() => {
        if (offer && show) {
            formik.setValues({
                numberOfStatic: offer.staticOffers || ``,
            });
        }
    }, [offer, show]);

    return (
        <Modal
            show={show}
            onHide={() => handleClose()}
            dialogClassName="w-full bg-white p-3 shadow rounded-md overflow-scroll md:w-[500px] md:max-h-[80vh]"
        >
            <Modal.Header>
                <Modal.Title className="text-lg font-semibold">Update Offer</Modal.Title>
            </Modal.Header>
            <Modal.Body className="my-5">
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
                    <FormControl
                        fullWidth
                        size="small"
                        error={formik.touched.numberOfStatic && Boolean(formik.errors.numberOfStatic)}
                        disabled={specificOfferLoading}
                    >
                        <InputLabel id="numberOfStatic-label">Number Of Static</InputLabel>
                        <Select
                            labelId="numberOfStatic-label"
                            id="numberOfStatic"
                            name="numberOfStatic"
                            label="Number Of Static"
                            value={formik.values.numberOfStatic || ''}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                        >
                            <MenuItem value="STATIC1">STATIC 1</MenuItem>
                            <MenuItem value="STATIC2">STATIC 2</MenuItem>
                            <MenuItem value="STATIC3">STATIC 3</MenuItem>
                            <MenuItem value="STATIC4">STATIC 4</MenuItem>
                            <MenuItem value="STATIC5">STATIC 5</MenuItem>
                            <MenuItem value="STATIC6">STATIC 6</MenuItem>
                            <MenuItem value="STATIC7">STATIC 7</MenuItem>
                            <MenuItem value="STATIC8">STATIC 8</MenuItem>
                            <MenuItem value="NO_STATIC">NO STATIC</MenuItem>
                        </Select>
                        {formik.touched.numberOfStatic && formik.errors.numberOfStatic && (
                            <FormHelperText>{String(formik.errors.numberOfStatic)}</FormHelperText>
                        )}
                    </FormControl>
                </form>
            </Modal.Body>
            <Modal.Footer className="flex items-center justify-center gap-2 mt-5">
                <ButtonBase
                    innerText="Cancel"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={() => {
                        handleClose();
                        formik.resetForm();
                    }}
                    disabled={specificOfferLoading || updateOfferLoading}
                />
                <ButtonBase
                    innerText="Update Offer"
                    className="bg-yellow-600 hover:bg-yellow-800 rounded border-none px-4 py-2 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={formik.handleSubmit}
                    disabled={specificOfferLoading || !formik.values.numberOfStatic || updateOfferLoading}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default UpdateOfferModal;
